import { toast } from 'react-toastify';
import {
	foodEnvironmentReady,
	foodOpEnqueuedElsewhere,
	foodOpResult,
	foodOpsDrained,
	foodSyncState,
	setFoodQueueHooks,
	tellFoodRejections,
} from '../actions/food';
import { setFoodBudgetSource, setFoodWriteBarrier } from './foodRequests';
import { startLegacyReset } from './legacyReset';
import { persistFood } from './persistFood';
import { describeOp } from './applyOp';

/**
 * Starts the food store's machinery on the page's store (docs/local-first-food-data.md, phases 2
 * to 4): legacy food requests name the store's budget and wait for its queued ops to be sent, the
 * calendar's and meal ideas' UI state is emptied when the store moves to another budget or user by
 * another route than a switch (legacyReset.js), the store is persisted, the op queue reports into the
 * store and sends on reconnect and on becoming visible, and a logout in another tab stops this one
 * writing and reloads it (`reload`). Loading is AppRouter's, once the player is known; its first
 * load also sends the queue. A change the server refuses is told through `notify`, a toast, once
 * (tellFoodRejections). Returns a function that stops it all.
 *
 * The reload is what keeps this tab usable. Its database is closed and its queue stopped for good,
 * and the session is gone, so a tab left running would take changes it could neither keep nor send,
 * even after someone signed in again in the other tab. Reloaded, it starts over: at the sign-in
 * page, or with a new database and queue for whoever is signed in by then.
 */
export function startFood(store, {
	notify = (message) => toast.error(message),
	reload = () => window.location.reload(),
} = {}) {
	setFoodBudgetSource(() => store.getState().food.budgetId);
	setFoodWriteBarrier(() => store.dispatch(foodOpsDrained()));
	setFoodQueueHooks({
		onResult: (record, meta) => {
			store.dispatch(foodOpResult(record));
			// Told by the tab that sent it. Another tab leaves it to that one.
			if(!(meta && meta.remote)) {
				store.dispatch(tellFoodRejections([record]));
			}
		},
		onEnqueued: (op) => store.dispatch(foodOpEnqueuedElsewhere(op)),
		onState: (state) => store.dispatch(foodSyncState(state)),
		onRejected: (records, entities) => records.forEach((record) => {
			notify(`${describeOp(record.op, entities)} couldn't be saved: ${record.result.error}`);
		}),
	});
	const stopReset = startLegacyReset(store);
	const persisting = persistFood(store);
	let stopChannel = () => {};
	let stopWatching = () => {};
	foodEnvironmentReady().then((env) => {
		if(env.queue) {
			env.queue.pruneResults();
			stopWatching = env.queue.watch();
		}
		if(!env.channel) {
			return;
		}
		const onMessage = (event) => {
			if(event.data && event.data.kind === 'logout') {
				persisting.stop();
				if(env.queue) {
					env.queue.stop();
				}
				env.session = null;
				env.db.close();
				reload();
			}
		};
		env.channel.addEventListener('message', onMessage);
		stopChannel = () => env.channel.removeEventListener('message', onMessage);
	});
	return () => {
		stopReset();
		persisting.stop();
		stopChannel();
		stopWatching();
		setFoodQueueHooks(null);
		setFoodWriteBarrier(null);
		setFoodBudgetSource(null);
	};
}
